/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
// @mui
import { List, Button, Stack, Box, Divider } from '@mui/material';
// Components
import { shallowEqual } from 'react-redux';
import { addScriptRowData, getCallScriptSection, getInboundScripts } from 'src/redux/slices/callScript';
import { getPracticeTreatmentList } from 'src/redux/slices/practiceTreatment';
import { getPracticeItem } from 'src/redux/slices/practice';
import useFormsFlags from 'src/hooks/useFormsFlags';
import Iconify from '../../Iconify';
import NavList from './NavList';
import { ConsentBuilderPopUp, ReviewLinkPopUp } from '../../dialogPopUp';

import SendFormsDialog from '../../dialogPopUp/SendFormsDialog';
import { OpenCallScriptPopup } from '../../fullScreenPopUps';
import CustomMenuItem from '../CustomMenuItem';
// Redux
import { clearConsentData } from '../../../redux/slices/lead';
import { openCreateLeadDialog, openDentallyBookingDialog } from '../../../redux/slices/createLeadDialog';
import { dispatch, useSelector } from '../../../redux/store';
// hooks/routes/constants
import { useAuth, useToggle, useConsentToggle } from '../../../hooks';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { allowedTitlesVertical } from '../../../constants/commonConstants';
import { localStorageRemoveItem } from '../../../utils/storage-available';
import { isNavItemHidden, titleToSlug } from '../hiddenNavItems';
// style
import { createButton, ListSubheaderStyle } from './style';

const NavSectionVertical = ({ navConfig, sx, isPractitioner, ...other }) => {

    const { practice } = useSelector((state) => ({
        practice: state.practice?.practice
    }), shallowEqual);

    const filterNHSNav = practice?.preferences?.is_accepting_nhs_patients;

    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const choicesData = useSelector((state) => state.practiceTreatment?.choicesData?.results || []);
    const permissions = useSelector((state) => state.permissions?.permissions, shallowEqual);
    const [anchorEl, setAnchorEl] = useState(null);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const navigate = useNavigate();

    // Consent form flags — read from feature flags (replaces the old choicesData derivation)
    const { v1Enabled, v2Enabled } = useFormsFlags();
    const [consentV2Open, setConsentV2Open] = useState(false);

    // Prefetch inbound scripts on mount for faster OpenCallScriptPopup loading
    useEffect(() => {
        if (practiceId) {
            dispatch(getInboundScripts(practiceId));
        }
    }, [practiceId]);

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const createHandleClose = () => setAnchorEl(null);

    const { user } = useAuth();

    const getUserList = () => results.filter(el => el.user && el.user.id === user?.pk);

    const {
        toggle: openCallScript,
        onOpen: onOpencallScript,
        onClose: onCloseCallScript
    } = useToggle();

    const {
        toggle: ConsentBuilder,
        onOpen: onOpenConsent,
        onClose: onCloseConsent
    } = useConsentToggle();

    const {
        toggle: openReview,
        onOpen: onOpenReview,
        onClose: onCloseReview
    } = useToggle();

    const onCloseCallScriptPopup = () => {
        const commonParams = {
            practiceId,
            limit: 10,
            offset: 0,
        };
        [false, true].forEach((isTemplate) => {
            dispatch(getCallScriptSection({ ...commonParams, isTemplate }));
        });
        onCloseCallScript();
    };

    const handleReviewClick = () => { onOpenReview(); createHandleClose(); };
    const handleClickNewLead = () => {
        localStorageRemoveItem('localTreatmentData');
        dispatch(openCreateLeadDialog());
        createHandleClose();
    };
    const handleClickBookDentally = () => {
        dispatch(openDentallyBookingDialog());
        createHandleClose();
    };
    const handleClickConsentV1Click = () => {
        onOpenConsent();
        dispatch(clearConsentData());
        createHandleClose();
    };
    const handleClickConsentV2Click = () => {
        setConsentV2Open(true);
        createHandleClose();
    };
    const handleClickStripePaymentClick = () => { navigate(PATH_DASHBOARD.createStripePayment.root); createHandleClose(); };
    const handleClickCallScriptClick = () => {
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: false }));
        dispatch(getCallScriptSection({ practiceId, limit: 10, offset: 0, isTemplate: true }));
        dispatch(addScriptRowData(''));
        dispatch(getPracticeTreatmentList(practiceId));
        dispatch(getPracticeItem(practiceId));
        onOpencallScript();
        // navigate(PATH_DASHBOARD.user.callScripts, { state: { callScriptPopupOpen: true } });
        createHandleClose();
    };
    const handleClickNewCampaign = () => { navigate(PATH_DASHBOARD.campaign.campaign); createHandleClose(); };
    const handleClickBookingWhatsApp = () => { navigate(PATH_DASHBOARD.whatsapp.root(practiceId)); createHandleClose(); };

    return (
        <Stack
            sx={{
                pb: '24px',
                ...sx,
            }}
            {...other}>
            <Stack sx={{ margin: '30px 0 12px', width: '100%', padding: '0 16px' }}>
                {isPractitioner ? null : <Button
                    sx={createButton}
                    variant='contained'
                    onClick={handleClick}
                    endIcon={<Iconify icon={anchorEl ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} />}
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                >
                    Create
                </Button>}
                <CustomMenuItem
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={createHandleClose}
                    handleClickCallScriptClick={handleClickCallScriptClick}
                    handleClickConsentV1Click={handleClickConsentV1Click}
                    handleClickConsentV2Click={handleClickConsentV2Click}
                    handleClickNewLead={handleClickNewLead}
                    handleClickStripePaymentClick={handleClickStripePaymentClick}
                    handleReviewClick={handleReviewClick}
                    handleClickNewCampaign={handleClickNewCampaign}
                    handleClickBookingWhatsApp={handleClickBookingWhatsApp}
                    dentallyEnabled={dentallyEnabledGlobal && !!getUserList()[0]?.user?.is_superuser}
                    handleClickBookDentally={handleClickBookDentally}
                    v1Enabled={v1Enabled}
                    v2Enabled={v2Enabled}
                />
            </Stack>
            {navConfig.map((group, groupIndex) => {
                const renderedItems = group.items.map((list) => {
                    const normalisedTitle = list?.title?.toLowerCase?.() || '';
                    const isSuperAdminItem = normalisedTitle === 'super admin';
                    const canRenderSuperAdmin = isSuperAdminItem && getUserList()[0]?.user?.is_superuser;
                    const isAllowed = allowedTitlesVertical.includes(normalisedTitle) || canRenderSuperAdmin;

                    if (!isAllowed) {
                        return null;
                    }

                    // Feature permission check.
                    // - Default behaviour (fail-open): show until permissions resolve
                    //   AND the feature is explicitly 'hidden'. Avoids flashing
                    //   "missing" features to a user mid-load when their nav is fine.
                    // - `requirePermission: true` (fail-closed): hide until permissions
                    //   resolve AND the feature is explicitly granted ('view' or
                    //   'manage'). Used for items where flashing them on initial load
                    //   is worse than not showing them while we wait.
                    if (list.feature) {
                        const featurePermission = permissions?.[list.feature];
                        if (list.requirePermission) {
                            if (featurePermission !== 'view' && featurePermission !== 'manage') {
                                return null;
                            }
                        } else if (permissions && Object.keys(permissions).length > 0) {
                            if (featurePermission === 'hidden') {
                                return null;
                            }
                        }
                    }

                    // Website annotation is always visible — sessions stored in our backend,
                    // ClickUp config is optional. The session picker handles "no website" gracefully.

                    if (normalisedTitle === 'dentally') {
                        if (!dentallyEnabledGlobal) {
                            return null;
                        }
                    }

                    // Filter children by feature permissions — same fail-open / fail-closed
                    // distinction as the parent above.
                    const featureFilteredChildren = list.children?.filter((child) => {
                        if (!child.feature) return true;
                        const childPerm = permissions?.[child.feature];
                        if (child.requirePermission) {
                            return childPerm === 'view' || childPerm === 'manage';
                        }
                        if (permissions && Object.keys(permissions).length > 0) {
                            return childPerm !== 'hidden';
                        }
                        return true;
                    });

                    // Practice nav preferences — cosmetic hide for sub-views
                    const hiddenNavItems = practice?.preferences?.hidden_nav_items || [];
                    const navPrefFilteredChildren = featureFilteredChildren?.filter((child) => {
                        if (child.divider) return true;
                        return !isNavItemHidden(titleToSlug(child?.title), hiddenNavItems);
                    });

                    // Filter Marketing Brief child by enableClickup
                    const clickupFilteredChildren = navPrefFilteredChildren?.filter((child) => {
                        if (child?.title?.toLowerCase() === 'marketing brief') {
                            const clickupCfg = (choicesData || []).find(
                                (c) => String(c?.name || '').trim().toLowerCase() === 'clickup config'
                            );
                            const enableCu = String(clickupCfg?.metadata?.enableClickup ?? '').trim().toLowerCase();
                            return enableCu === 'yes' || enableCu === 'true' || enableCu === '1';
                        }
                        return true;
                    });

                    const shouldFilterNHSChildren = normalisedTitle === 'active leads' || normalisedTitle === 'lead management';

                    if (shouldFilterNHSChildren) {
                        const sublist = {
                            ...list,
                            children: clickupFilteredChildren?.filter((child) => filterNHSNav || child?.title !== 'NHS'),
                        };

                        return (
                            <NavList
                                key={list.title + list.path}
                                data={sublist}
                                depth={1}
                                hasChildren={!!list.children}
                            />
                        );
                    }

                    const listWithFilteredChildren = clickupFilteredChildren ? { ...list, children: clickupFilteredChildren } : list;

                    return (
                        <NavList
                            key={list.title + list.path}
                            data={listWithFilteredChildren}
                            depth={1}
                            hasChildren={!!list.children}
                        />
                    );
                }).filter(Boolean);

                if (!renderedItems.length) {
                    return null;
                }

                return (
                    <Box key={group.subheader || `group-${groupIndex}`} sx={{ width: '100%' }}>
                        {group.subheader && (
                            <ListSubheaderStyle component='div' disableSticky>
                                {group.subheader}
                            </ListSubheaderStyle>
                        )}

                        <List disablePadding sx={{ px: 2, bgcolor: 'transparent' }}>
                            {renderedItems}
                        </List>

                        {groupIndex < navConfig.length - 1 && (
                            <Divider flexItem sx={{ my: 2, borderColor: (theme) => theme.palette.divider }} />
                        )}
                    </Box>
                );
            })}
            {ConsentBuilder && v1Enabled && <ConsentBuilderPopUp
                open={ConsentBuilder}
                onClose={onCloseConsent}
            />}
            {consentV2Open && <SendFormsDialog
                open={consentV2Open}
                onClose={() => setConsentV2Open(false)}
                practiceId={practiceId}
            />}
            {openReview && <ReviewLinkPopUp
                open={openReview}
                onClose={onCloseReview}
            />}
            {openCallScript && <OpenCallScriptPopup
                open={openCallScript}
                headerButtons
                onClose={() => onCloseCallScriptPopup()}
                type={'callScript'}
            />}
        </Stack>
    );
};

NavSectionVertical.propTypes = {
    sx: PropTypes.object,
    navConfig: PropTypes.array,
    isPractitioner: PropTypes.bool
};

export default NavSectionVertical;
